import { X } from "lucide-react";
import { useRef } from "react";

import Loading from "components/Loading";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "components/ui/dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // Runs once the dialog has finished fading out, to reset its content unseen.
  afterLeave?: () => void;
  loading: boolean;
  // A message to show, or `true` for a generic one.
  error?: boolean | string;
  title: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  children: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  afterLeave,
  loading,
  error,
  title,
  confirmLabel,
  confirmDisabled = false,
  children,
}) => {
  const opener = useRef<HTMLElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          opener.current = document.activeElement as HTMLElement | null;
        }}
        // Radix fires this once the content unmounts, after its exit animation.
        // Without a DialogTrigger it would drop focus, so return it to the opener.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          opener.current?.focus();
          afterLeave?.();
        }}
        // A column capped to the viewport, so long content scrolls between the
        // fixed title and buttons instead of running off screen.
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 rounded-2xl border-0 bg-white"
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-3 top-3 size-8 text-muted-foreground"
          aria-label="Close"
          onClick={onClose}
          disabled={loading}
        >
          <X className="size-4" />
        </Button>
        <DialogTitle className="mb-2 pr-8 leading-normal tracking-normal">
          {title}
        </DialogTitle>
        <div className="mb-4 min-h-0 overflow-y-auto border-b border-t border-gray-200 py-4">
          {children}
        </div>
        {error && (
          <div role="alert" className="mb-4 rounded bg-red-50 p-3 text-red-700">
            {typeof error === "string"
              ? error
              : "Got an unexpected error, please contact or try again later."}
          </div>
        )}
        <div className="flex justify-end space-x-2">
          {loading ? (
            <Loading />
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={onConfirm} disabled={confirmDisabled}>
                {confirmLabel}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
